import React, { useCallback, useMemo } from 'react'
import { Box } from '@mui/material'
import { isSuccess, useFirebase } from 'src/firebase'
import { ReferralTypes } from 'src/legacy/models/firebase'
import { checkIfKaiserHealthPlan } from 'src/participant/participantUtils'
import { useIsAdmin, useIsCareCoordinator, useIsManager } from 'src/session/useSession'
import { usePanelPatientUid } from '../../dashboard/panelContext'
import {
  CardFooter,
  FieldRow,
  FieldSection,
  LabeledCheckbox,
  LabeledSelect,
  LabeledText,
  noop,
} from '../fieldPrimitives'
import { BILLING_INFO_SCHEMA } from './patientRecordSchema'
import { makeFieldSetter, useCardAutosave } from './useCardAutosave'
import { usePatientRecord } from './usePatientRecord'
import { useUpdatePatientRecord } from './useUpdatePatientRecord'

type BillingValues = {
  memberID: string
  groupID: string
  externalReferenceID: string
  referralType: string
  referralName: string
  isFreeRider: boolean
  excludedFromCalculations: boolean
}

const BillingInfoPanel = () => {
  const uid = usePanelPatientUid()
  const { loading, patient, details } = usePatientRecord(uid)
  const update = useUpdatePatientRecord(uid)
  const isAdmin = useIsAdmin()
  const isManager = useIsManager()
  const isCareCoordinator = useIsCareCoordinator()
  const canSeeInsuranceIDs = isAdmin || isManager || isCareCoordinator

  const referralTypes = useFirebase<ReferralTypes>('referralTypes')
  const referralTypeOptions = useMemo(
    () =>
      isSuccess(referralTypes)
        ? Object.values(referralTypes.data ?? {})
            .map(({ title }) => title)
            .sort()
            .map((title) => ({ value: title, label: title }))
        : [],
    [referralTypes]
  )

  const initial = useMemo<BillingValues>(
    () => ({
      memberID: details.memberID ?? '',
      groupID: details.groupID ?? '',
      externalReferenceID: details.externalReferenceID ?? '',
      referralType: details.referralType ?? '',
      referralName: details.referralName ?? '',
      isFreeRider: patient?.privateUserData?.isFreeRider ?? false,
      excludedFromCalculations: details.excludedFromCalculations ?? false,
    }),
    [
      details.memberID,
      details.groupID,
      details.externalReferenceID,
      details.referralType,
      details.referralName,
      patient?.privateUserData?.isFreeRider,
      details.excludedFromCalculations,
    ]
  )

  const save = useCallback(
    (next: BillingValues) =>
      update(
        undefined,
        {
          memberID: next.memberID,
          groupID: next.groupID,
          externalReferenceID: next.externalReferenceID,
          referralType: next.referralType,
          referralName: next.referralName,
          excludedFromCalculations: next.excludedFromCalculations,
        },
        { isFreeRider: next.isFreeRider }
      ),
    [update]
  )

  const { values, setValues, error, status } = useCardAutosave(initial, BILLING_INFO_SCHEMA, save)
  const set = makeFieldSetter(setValues)

  return (
    <FieldSection>
      {canSeeInsuranceIDs && (
        <FieldRow columns={3}>
          <LabeledText
            label={
              checkIfKaiserHealthPlan(patient?.credentialingHealthPlan)
                ? 'Medical Record Number'
                : 'Insurance Member ID'
            }
            value={values.memberID}
            onChange={set('memberID')}
          />
          <LabeledText label="Insurance Group ID" value={values.groupID} onChange={set('groupID')} />
          <LabeledText
            label="External Reference ID"
            value={values.externalReferenceID}
            onChange={set('externalReferenceID')}
          />
        </FieldRow>
      )}
      <FieldRow columns={3}>
        <LabeledText label="Billing ID" value={patient?.billingID ?? ''} onChange={noop} disabled />
        <LabeledSelect
          label="Referral type"
          value={values.referralType}
          onChange={set('referralType')}
          options={referralTypeOptions}
        />
        <LabeledText label="Referral name" value={values.referralName} onChange={set('referralName')} />
      </FieldRow>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <LabeledCheckbox label="Is free rider" checked={values.isFreeRider} onChange={set('isFreeRider')} />
        <LabeledCheckbox
          label="Excluded from billing and customer reporting"
          checked={values.excludedFromCalculations}
          onChange={set('excludedFromCalculations')}
        />
      </Box>
      <CardFooter loading={loading} status={status} error={error} />
    </FieldSection>
  )
}

export default BillingInfoPanel
